<template>
  <footer
    class="form-page__footer"
    :class="{ 'form-page__footer--shadow': shadow }"
  >
    <div class="form-page__footer__container">
      <div class="text--right">
        <a
          v-if="step === 2 && status !== isLoading"
          :title="$t('Common.cancel')"
          class="mr-2"
          @click.prevent="openCancelDialog"
        >{{ $t('Common.cancel') }}</a>

        <Button
          :disabled="status === isLoading"
          :loading="status === isLoading"
          type="submit"
          @click="submit"
        >
          {{ $t(`${namespace}.conclude`) }}
        </Button>
      </div>
    </div>

    <CancelDialog
      :visible="cancelDialog"
      @close="closeCancelDialog"
    />
  </footer>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { catchError } from 'corp-front-vue-commons/src/utils/functions'
import CancelDialog from './CancelDialog'

export default {
  name: 'Footer',
  props: {
    kind: {
      type: String
    },
    status: {
      type: Number
    },
    step: {
      type: Number,
      default: 1
    },
    data: {
      type: Object,
      required: true
    }
  },
  components: {
    CancelDialog
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany
    }),

    companyId () {
      const routeParamId = this.$route.params && this.$route.params.id
        ? this.$route.params.id
        : null
      const companyId = this.selectedCompany && this.selectedCompany.id
        ? this.selectedCompany.id
        : null

      return routeParamId || companyId
    },

    type () {
      if (this.kind === 'costCenter') {
        return this.$t('Common.costCenters')
      }

      if (this.kind === 'employee') {
        return this.$t('Common.employees')
      }

      if (this.kind === 'project') {
        return this.$t('Common.projects')
      }

      if (this.kind === 'incentives') {
        return this.$t('Common.incentives')
      }

      if (this.kind === 'approvePayment') {
        return this.$t('Common.charges')
      }

      return ''
    },

    isLoading: () => STATUS_IS_LOADING,
    isSuccess: () => STATUS_SUCCESS,
    isFailed: () => STATUS_FAILED
  },
  data () {
    return {
      namespace: 'Views.BulkImportPage.footer',
      shadow: true,
      cancelDialog: false
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    async execute () {
      this.$emit('updateRequestStatus', STATUS_IS_LOADING)

      try {
        const { id } = this.data
        let payload = null
        const hasStatusAttribute = this.data && this.data.data
          ? this.data.data.some(line => line.status)
          : false

        if (this.kind === 'employee') {
          payload = this.data
          payload.updatedData = []
        }

        if (!hasStatusAttribute && payload && payload.conflicts > 0) {
          payload.data.forEach(line => {
            line.status = { code: 'insert', register: null }
          })
        }

        if (this.kind === 'approvePayment') {
          await this.$apiService.executeMassiveOrder(id)
        } else if (this.kind === 'incentives') {
          const { uploadId } = this.data
          await this.$apiService.incentives.uploadWorker(uploadId)
        } else {
          await this.$apiService.company.executeBulkOperation(this.companyId, id, payload)
        }

        this.$emit('updateStep', {
          currentStep: 3,
          type: 'success',
          payload: this.data
        })

        this.$emit('updateRequestStatus', STATUS_SUCCESS)
      } catch (error) {
        const message = this.kind === 'approvePayment'
          ? this.$t(`${this.namespace}.approvePaymentErrorMessage`).replace(/\[TYPE\]/g, this.type.toLowerCase())
          : this.$t(`${this.namespace}.errorMessage`).replace(/\[TYPE\]/g, this.type.toLowerCase())

        this.$emit('updateRequestStatus', STATUS_FAILED)
        this.showAlert({ type: 'error', message })
        catchError(error)
      }
    },

    redirectToList () {
      const message = this.kind === 'approvePayment'
        ? this.$t(`${this.namespace}.approvePaymentSuccessMessage`).replace(/\[TYPE\]/g, this.type.toLowerCase())
        : this.$t(`${this.namespace}.successMessage`).replace(/\[TYPE\]/g, this.type.toLowerCase())

      this.showAlert({ type: 'success', message })

      if (this.kind === 'costCenter') {
        this.$router.push({ name: 'CostCenters' })
        return
      }

      if (this.kind === 'employee') {
        this.$router.push({ name: 'Employees' })
        return
      }

      if (this.kind === 'project') {
        this.$router.push({ name: 'Projects' })
        return
      }

      if (this.kind === 'approvePayment') {
        this.$router.push({ name: 'ApprovePayment' })
      }

      if (this.kind === 'incentives') {
        this.$router.push({ name: 'Incentives' })
      }
    },

    async submit () {
      if (this.step === 2) {
        await this.execute()
        return
      }

      this.redirectToList()
    },

    handleShadow () {
      this.shadow = !((window.innerHeight + window.scrollY) >= document.body.scrollHeight)
    },

    openCancelDialog () {
      this.cancelDialog = true
    },

    closeCancelDialog (redirect) {
      this.cancelDialog = false
      if (redirect) this.$router.go(-1)
    }
  },
  mounted () {
    this.handleShadow()
    window.addEventListener('scroll', this.handleShadow)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.handleShadow)
  }
}
</script>
